/* eslint-disable no-nested-ternary */
import styled, { css, keyframes } from 'styled-components';

const errorBounce = keyframes`
 0% {
    transform: translateX(5px);
  }
  25% {
    transform: translateX(-5px);
  }
  50% {
    transform: translateX(5px);
  }
  75% {
    transform: translateX(-5px);
  }
  100% {
    transform: translateX(5px);
  }
`;

const Font = css`
  font-family: 'Mulish', sans-serif;
  font-weight: 400;
  font-style: normal;
`;

export const TextAreaContainer = styled.div<{
  hasError: boolean;
  maxLength: boolean;
  rows: number;
  disabled: boolean;
}>`
  height: ${({ rows }) => rows * 23 + 10}px;
  padding: 10px;
  border-radius: 8px;
  border: 0.5px solid;
  border-color: ${({ theme, hasError, maxLength, disabled }) => {
    if (hasError) {
      return theme.status.error.color;
    }
    if (disabled) {
      return theme.grayCC;
    }
    if (maxLength) {
      return theme.status.warning.hover;
    }

    return theme.grayB8;
  }};
`;

export const TextAreaWrapper = styled.div`
  padding-right: 8px;
`;

export const TextArea = styled.textarea<{
  isMobile: boolean;
  hasError: boolean;
  disabled: boolean;
}>`
  border: none;
  outline: none;
  width: 100%;
  resize: none;
  color: ${({ theme, disabled }) => (disabled ? theme.grayB8 : theme.gray2D)};
  line-height: 22px;
  cursor: auto;
  ${Font}

  ${({ isMobile }) => css`
    font-size: ${isMobile ? '14px' : '16px'};
    letter-spacing: ${isMobile ? '0.0025em' : '0.005em'};
  `}

  ::placeholder {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme, disabled }) => (disabled ? theme.grayB8 : theme.grayA9)};
  }

  :placeholder-shown {
    text-overflow: ellipsis;
  }

  :disabled {
    background-color: ${({ theme }) => theme.white};
  }

  ${({ hasError, theme }) =>
    hasError &&
    css`
      color: ${theme.status.error.color};
      animation: ${errorBounce} 250ms linear;
      border-color: ${theme.status.error.color};
      &::placeholder {
        color: ${theme.status.error.color};
      }
    `}

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
    cursor: default;
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.grayB8};
    border-radius: 10px;
    cursor: default;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.grayB8};
    cursor: default;
  }
`;

export const HelperContainer = styled.div`
  margin-top: 4px;
  display: flex;
`;

export const HelperText = styled.div<{
  disabled: boolean;
}>`
  ${Font}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.004em;
  color: ${({ theme, disabled }) => (disabled ? theme.grayB8 : theme.gray80)};
  flex: 1 1 auto;
  text-align: left;

  & ~ div {
    flex: 0 1 auto;
  }
`;

export const LengthMessage = styled.div<{
  disabled: boolean;
}>`
  ${Font}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 0.004em;
  color: ${({ theme, disabled }) => (disabled ? theme.grayB8 : theme.gray80)};
  margin-left: 4px;
  flex: 1 1 auto;
  text-align: left;

  & ~ div {
    flex: 0 1 auto;
  }
`;

export const CharCounter = styled.div<{
  disabled: boolean;
  hasError: boolean;
}>`
  ${Font}
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme, disabled, hasError }) =>
    disabled ? theme.grayB8 : hasError ? theme.status.error.color : theme.gray80};
  flex: 1 1 auto;
  text-align: right;
`;

export const ErrorMessage = styled.div`
  ${Font}
  ${({ theme }) => css`
    color: ${theme.status.error.color};
    line-height: ${theme.input.error.lineHeight}px;
    font-size: ${theme.input.error.fontSize}px;
  `};

  margin-left: 4px;
  text-align: left;
  flex: 1 1 auto;

  & ~ div {
    flex: 0 1 auto;
  }
`;
